import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import { InputCurrency, InputParlevelQuantity } from '../../components/input';
import { ProductSelector } from '../../components/selector';
import { MEASUREMENT_UNIT_SHORT_NAME } from '../../../common/constants';
import { Text, TypographyParagraph } from '../../components';
import { PrimaryButton } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PickerButton from '../../components/picker/PickerButton/PickerButton';
import useDialogSaveButton from '../../hooks/useDialogSaveButton';
import PlanogramItemSalesLastXDays from './PlanogramItemSalesLastXDays';
import { resolveRestrictedProductTags } from '../../../common/utils/planograms/planogram';
import { shouldApplyDynamicParlevels } from '../../../common/utils/planograms/dynamicParlevels';
import { useUserContext } from '../../../common/utils';
import ExpiryDateButtons from './ExpiryDateButtons';

const QUANTITY_FIELDS = [
	{ field: 'presentProductQuantity', label: 'Present' },
	{ field: 'spoiledProductQuantity', label: 'Spoiled' },
	{ field: 'removedProductQuantity', label: 'Removed' },
];

const PlanogramItemEdit = ({
	planogramItem,
	setPlanogramItemValue,
	usesDynamicParlevels,
	enablePlanogramItemCapacity,
	isMarket,
	planogramItemDaysSinceLastSale,
	defaultShowSalesLastXDays,
	pos,
}) => {
	const { tenantSettings, isDriver, isAVC } = useUserContext();
	const styles = useThemedStyles(createStyles);

	const isParlevelLocked = shouldApplyDynamicParlevels(planogramItem, pos);
	const { isSaveButtonPressed, onSaveButtonPress } = useDialogSaveButton();
	const hasPriceBook = Boolean(planogramItem?.priceBook);
	// Lock price edits when a price book applies so mobile matches enforced pricing rules.
	const priceLocked = Boolean(planogramItem?.priceBookLocked || planogramItem?.priceBookApplied || hasPriceBook);

	const restrictedProductTags = resolveRestrictedProductTags(pos);

	const planogramEditDisabled = isDriver && isAVC;

	// Focus lands on the first quantity (Present; Par level for ingredients, which have no Present),
	// in the same cases that used to focus an editable Par level.
	const [autoFocus, setAutoFocus] = useState(!isParlevelLocked && !planogramEditDisabled);
	useEffect(() => {
		setAutoFocus(false);
	}, []);

	const priceLockMessage = priceLocked
		? `Price locked by ${planogramItem?.priceBook?.name || 'price book'}`
		: null;
	const priceChangeHandler = (priceLocked || planogramEditDisabled)
		? undefined
		: setPlanogramItemValue(planogramItem.id, 'price');
	const capacityChangeHandler = planogramEditDisabled
		? undefined
		: v => {
			if (v > 99) {
				return;
			}
			return setPlanogramItemValue(planogramItem.id, 'capacity')(v);
		};

	return (
		<View style={styles.container}>
			<Text style={styles.title}>{ !isMarket && `${planogramItem.selection}. `}{planogramItem.product?.name}</Text>

			<View style={styles.field}>
				<Text style={styles.label}>Product</Text>
				<ProductSelector
					initialFilter={{
						isIngredient: Boolean(planogramItem.isIngredient),
					}}
					blockDisabledProducts={true}
					restrictedProductTags={restrictedProductTags}
					initialValue={planogramItem.product}
					onChange={setPlanogramItemValue(planogramItem.id, 'product')}
					customButton={({ onPress, label }) => (
						<PickerButton disabled={planogramEditDisabled} onPress={onPress} style={{ paddingTop: 12, paddingBottom: 10 }}>
							{label}
						</PickerButton>
					)}
				/>
			</View>

			{
				!planogramItem.isIngredient && (
					<View style={styles.quantityRow}>
						{QUANTITY_FIELDS.map(({ field, label }) => (
							<View key={field} style={[styles.field, styles.quantityField]}>
								<Text style={styles.label}>{label}</Text>
								<InputParlevelQuantity
									autoFocus={autoFocus && field === 'presentProductQuantity'}
									style={styles.quantityInput}
									onChange={setPlanogramItemValue(planogramItem.id, field)}
									// Same as the list counters: no value yet reads as 0.
									value={planogramItem[field] || 0}
								/>
							</View>
						))}
					</View>
				)
			}

			<View style={styles.row}>
				<View style={styles.field}>
					<Text style={styles.label}>Par level</Text>
					<InputParlevelQuantity
						autoFocus={autoFocus && planogramItem.isIngredient}
						disabled={isParlevelLocked || planogramEditDisabled}
						product={planogramItem.product}
						suffix={planogramItem.product && MEASUREMENT_UNIT_SHORT_NAME[planogramItem.product.measurementUnit]}
						onChange={v => {
							if (v > 99) {
								return;
							}
							return setPlanogramItemValue(planogramItem.id, 'parlevel')(v);
						}}
						value={planogramItem.parlevel}
					/>
				</View>
				{
					enablePlanogramItemCapacity && (
						<View style={styles.field}>
							<Text style={styles.label}>Capacity</Text>
							<InputParlevelQuantity
								disabled={planogramEditDisabled}
								product={planogramItem.product}
								suffix={planogramItem.product && MEASUREMENT_UNIT_SHORT_NAME[planogramItem.product.measurementUnit]}
								onChange={capacityChangeHandler}
								value={planogramItem.capacity}
							/>
						</View>
					)
				}
			</View>
			{ usesDynamicParlevels && !planogramEditDisabled && (
				<TypographyParagraph
					text={isParlevelLocked ? 'Set parlevel manually' : 'Use dynamic parlevel'}
					href={true}
					onPress={() => setPlanogramItemValue(planogramItem.id, 'parlevelUnlocked')(!planogramItem.parlevelUnlocked)}
				/>
			)}
			<View style={styles.row}>
				{
					!planogramItem.isIngredient && !isMarket && (
						<View style={styles.field}>
							<Text style={styles.label}>Price</Text>
							<InputCurrency
								onChange={priceChangeHandler}
								value={planogramItem.price}
								disabled={priceLocked || planogramEditDisabled}
							/>
							{priceLockMessage && (
								<TypographyParagraph
									style={styles.hint}
									text={priceLockMessage}
								/>
							)}
						</View>
					)
				}
				{
					!planogramItem.isIngredient && defaultShowSalesLastXDays && (
						<View style={styles.field}>
							<Text style={styles.label}>Sales last {planogramItemDaysSinceLastSale} days</Text>
							<PlanogramItemSalesLastXDays
								salesLast365Days={planogramItem.planogramItemStatistics?.salesLast365Days}
								salesLast30Days={planogramItem.planogramItemStatistics?.salesLast30Days}
								salesLast14Days={planogramItem.planogramItemStatistics?.salesLast14Days}
								salesLast7Days={planogramItem.planogramItemStatistics?.salesLast7Days}
								lastProductChangeTime={planogramItem.planogramItemStatistics?.lastProductChangeTime}
								created={planogramItem.created}
								dialogStyle={true}
							/>
						</View>
					)
				}
			</View>

			{
				(tenantSettings.useExpiryDates && Boolean(planogramItem.presentProductQuantity)) && (
					<View style={styles.field}>
						<Text style={styles.label}>Expiry dates</Text>
						<ExpiryDateButtons planogramItem={planogramItem} forceShowButtons={true} isDialog={true} />
					</View>
				)
			}

			<PrimaryButton
				size="compact"
				label="OK"
				loading={isSaveButtonPressed}
				onPress={onSaveButtonPress}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		gap: space.md,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		// Keep clear of the dialog's close button.
		paddingRight: 36,
	},
	row: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	field: {
		gap: space.xs,
	},
	// Present, Spoiled and Removed share one row, so each input takes a third.
	quantityRow: {
		flexDirection: 'row',
		gap: space.sm,
	},
	quantityField: {
		flex: 1,
	},
	quantityInput: {
		width: '100%',
	},
	label: {
		...type.caption,
		color: colors.textSecondary,
	},
	hint: {
		...type.caption,
		color: colors.textSecondary,
	},
});


export default React.memo(PlanogramItemEdit);
